import { createTheme, darken, lighten } from "@mui/material";

declare module '@mui/material/styles' {
	interface Palette {
		assetUrl: string
		informationAccent: {
			light: string;
			main: string;
			dark: string;
			contrastText: string;
		}
		textAccent: {
			light: string;
			main: string;
			dark: string;
			contrastText: string;
		}
		actionAccent: {
			light: string;
			main: string;
			dark: string;
			contrastText: string;
		}
		global: {
			main: string
		}
	}

	interface PaletteOptions {
		assetUrl: string
		informationAccent: {
			light: string;
			main: string;
			dark: string;
			contrastText: string;
		}
		textAccent: {
			light: string;
			main: string;
			dark: string;
			contrastText: string;
		}
		actionAccent: {
			light: string;
			main: string;
			dark: string;
			contrastText: string;
		}
		global: {
			main: string
		}
	}
}
export let theme = createTheme({
	palette: {
		assetUrl: '',
		text: {
			primary: "#151618",
			secondary: "#FFF"
			// primary: "#d003d0",
		},
		background: {
			default: '#FFF',
			paper: '#FFF'
		},
		primary: {
			main: "#151618",
			light: "#0370F3",
		},
		secondary: {
			light: '#EDECEC',
			main: '#CDD8DE',
			// main: '#d003d0',
			dark: '#B1BCC2',
		},
		textAccent: {
			light: '#8C9DA7',
			dark: '#4A4A4A',
			main: '#6f777f',
			// main: '#d003d0',
			contrastText: "#FFFFFF",
		},
		global: {
			main: '#f5f6f6'
		},
		informationAccent: {
			main: "#025AC4",
			light: "#95C2F9",
			dark: "#00B034",
			contrastText: "#FFFFFF",
		},
		actionAccent: {
			main: "#025AC4",
			light: "#95C2F9",
			dark: "#00B034",
			contrastText: "#FFFFFF",
		},
	},
	shape: {
		borderRadius: 4,
	},
	components: {
		MuiCheckbox: {
			styleOverrides: {
				root: {
					padding: '10px 10px 10px 0',
					"&:hover": {
						background: 'transparent'
					}
				},
				colorPrimary:  ({ theme }) => ({
					color: '#DDE5EA',
					'&:checked': {
						color: theme.palette.actionAccent.dark,
					},
				}),
			},
		},
		MuiLink: {
			styleOverrides: {
				root: ({ theme }) => ({
					color: theme.palette.primary.light,
					textDecoration: 'none',
					'&:hover': {
						color: darken(theme.palette.primary.light, 0.5),
					},
					'&:active': {
						color: darken(theme.palette.primary.light, 0.5),
					},
					'&:focus': {
						color: darken(theme.palette.primary.light, 0.5),
					}
				})
			}
		},
		MuiFormLabel: {
			styleOverrides: {
				root: ({ theme }) => ({
					color: theme.palette.textAccent.dark,
					fontSize: '14px',
					"&.Mui-focused": {
						color: theme.palette.textAccent.dark,
						fontSize: '14px',
					},
				})
			}
		},
		MuiInputBase: {
			styleOverrides: {
				root: ({ theme }) => ({
					backgroundColor: `${theme.palette.background.default} !important`,
					color: theme.palette.text.primary,
					borderRadius: '8px!important',
					"& .MuiOutlinedInput-notchedOutline": {
						border: `1px solid ${theme.palette.secondary.main}`,
						borderRadius: '8px',
						fontSize: '14px',
					},
					"& .MuiOutlinedInput-root": {
						backgroundColor: `${theme.palette.background.default} !important`,
						borderRadius: '8px !important',
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						border: `1px solid ${darken(theme.palette.secondary.main, 0.4)} !important`,
					},
					"&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
						borderColor: `${lighten(theme.palette.secondary.main, 0.4)} !important`
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: `${darken(theme.palette.secondary.main, 0.4)} !important`
					},
					'&:after, &:before': {
						display: 'none'
					},
					'& .Mui-disabled': {
						borderColor: `${lighten(theme.palette.secondary.main, 0.4)} !important`
					}
				}),
				input: ({ theme }) => ({
					backgroundColor: theme.palette.background.default,
					borderRadius: '8px',
					color: theme.palette.text.primary,
					fontFamily: 'Circular',
					fontSize: '12px',
					fontWeight: 400,
				}),
			}
		}
	}
});

theme = createTheme(theme, {
	typography: {
		fontFamily: ['Circular', 'sans-serif'].join(','),
		fontSize: 12,
		body1: {
			color: theme.palette.text.primary,
			fontFamily: 'Circular',
			fontSize: '12px',
			lineHeight: '16px',
			fontWeight: 400,
		},
		body2: {
			color: theme.palette.text.primary,
			fontFamily: 'Circular',
			fontSize: '12px',
			lineHeight: '17px',
			fontWeight: 600,
		},
		subtitle2: {
			color: theme.palette.text.primary,
			fontFamily: 'Circular',
			fontSize: '14px',
			lineHeight: '19px',
			fontWeight: 700,
		},
		subtitle1: {
			color: theme.palette.text.primary,
			fontFamily: 'Circular',
			fontSize: '12px',
			lineHeight: '17px',
			fontWeight: 400,
		},
		h2: {
			color: theme.palette.text.primary,
			fontFamily: 'Circular',
			fontSize: '56px',
			lineHeight: '78px',
			fontWeight: 800,
		},
		h3: {
			color: theme.palette.text.primary,
			fontFamily: 'Circular',
			fontSize: '48px',
			lineHeight: '62px',
			fontWeight: 800,
			marginBottom: '3rem'
		},
		h4: {
			color: theme.palette.text.primary,
			fontFamily: 'Circular',
			fontSize: '20px',
			lineHeight: '32px',
			fontWeight: 700,
			marginBottom: '1rem'
		}
	},
})
